import L from 'leaflet';
import 'leaflet.icon.glyph';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { Clock, MapPin, Menu, Settings } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import { Marker, Polyline, Popup, Tooltip, useMapEvents } from 'react-leaflet';
import { useMediaQuery } from 'react-responsive';
import { Badge, Col, Label, Media, Row, UncontrolledTooltip } from 'reactstrap';
import useDeepCompareEffect from 'use-deep-compare-effect';
import Icon from 'utility/icomoon';
import RotatedMarker from 'views/components/Map/components/RotatedMarker';
import SlidingPaneSharedLinkUnitInformation from 'views/screens/ExternalLogin/views/SharedLinkUnitLocation/SlidingPaneSharedLinkUnitInformation';
import { unit } from 'views/screens/Groups/schemas';
import { UNIT_FETCH_INTERVAL } from 'views/screens/Units/types';
import {
  getDatesDifferenceString,
  getIconByDatesDifference,
  GetMainStateById,
  GetMainStateName,
  GetMainStateStyle
} from 'views/screens/Units/utils';
import EventMarkerPulse from '../EventMarkerPulse';
import { env } from '@src/env';

export interface MapUnitsPublicOptions {
  showUnits: boolean;
  units: any[];
  showHistoricalPositions?: boolean;
  useLiveUnitFetchInterval?: boolean;
  startLiveUnitRerender?: any;
  setMapPositionToUnitBounds?: boolean;
  setMapPositionToPositionsBounds?: boolean;
  sharedLinkUnitLocation?: boolean;
  sharedLinkRoute?: boolean;
  usePopovers?: boolean;
  usePermanentPopover?: boolean;
  unitHistoricalPositions: any;
  mapOnSlidingPane?: boolean;
  showUnitHistoryOnlyNormalPositionsMarkers?: boolean;
  showConsoleEvents?: boolean;
  consoleEvents?: any[];
}

const MapUnitsPublic = (props: MapUnitsPublicOptions) => {
  const map = useMapEvents({
    moveend: () => {
      displayMarkers();
      if (showHistoricalPositions && sharedLinkRoute) {
        displayObjects();
      }
    }
  });
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const intl = useIntl();
  const fetchPositionInterval = useRef<any>();
  const primary = env.VITE_PRIMARY_COLOR;

  const [markersComponents, setMarkersComponents] = useState<JSX.Element[]>([]);
  const [openSharedLinkUnitInformationSlidingPane, setOpenSharedLinkUnitInformationSlidingPane] =
    useState<boolean>(false);
  const [openSharedLinkUnitInformationTooltip, setOpenSharedLinkUnitInformationTooltip] =
    useState<boolean>(!isDesktop ? true : false);
  const [historicalPositionsOnMapBounds, setHistoricalPositionsOnMapBounds] = useState<any>(null);
  const [unitPositionHistoryReady, setUnitPositionHistoryReady] = useState<boolean>(false);

  const {
    showUnits = true,
    units,
    showHistoricalPositions = false,
    useLiveUnitFetchInterval = false,
    startLiveUnitRerender,
    setMapPositionToUnitBounds = false,
    setMapPositionToPositionsBounds = false,
    sharedLinkUnitLocation = false,
    sharedLinkRoute = false,
    usePopovers = true,
    usePermanentPopover = false,
    unitHistoricalPositions,
    mapOnSlidingPane = false,
    showUnitHistoryOnlyNormalPositionsMarkers = false,
    showConsoleEvents = false,
    consoleEvents
  } = props;

  const zoomMapToUnit = (latLng) => {
    var ll;
    if (latLng instanceof L.LatLng) {
      ll = latLng;
    } else {
      ll = L.latLng(latLng);
    }
    if (mapOnSlidingPane) {
      map.flyToBounds([ll], {
        paddingBottomRight: [0, 0],
        paddingTopLeft: [0, 0],
        maxZoom: 15,
        animate: 15 !== map.getZoom()
      });
    } else {
      map.flyToBounds([ll], {
        paddingBottomRight: [550, 0],
        paddingTopLeft: [0, 70],
        maxZoom: 15,
        animate: 15 !== map.getZoom()
      });
    }
  };

  const zoomMapToUnitMobile = (latLng) => {
    if (!isDesktop) {
      var ll;
      if (latLng instanceof L.LatLng) {
        ll = latLng;
      } else {
        ll = L.latLng(latLng);
      }
      map.flyToBounds([latLng], {
        paddingBottomRight: [0, 0],
        paddingTopLeft: [0, 400],
        maxZoom: 15,
        animate: 15 !== map.getZoom()
      });
    }
  };

  const zoomMapToPositions = (historicalPositions) => {
    if (
      setMapPositionToPositionsBounds &&
      historicalPositions?.positions?.filter(
        (position, index) =>
          position?.latitude != null &&
          position?.latitude !== 0 &&
          position?.longitude != null &&
          position?.longitude !== 0
      ).length > 0
    ) {
      map.flyToBounds(
        L.latLngBounds(
          historicalPositions?.positions
            .filter(
              (position, index) =>
                position?.latitude != null &&
                position?.latitude !== 0 &&
                position?.longitude != null &&
                position?.longitude !== 0
            )
            .map((position) => L.latLng(position.latitude, position.longitude))
        ),
        {
          paddingBottomRight: [550, 0],
          paddingTopLeft: [0, 70],
          maxZoom: 15,
          animate: 15 !== map.getZoom()
        }
      );
    }
  };

  const cleanup = () => {
    if (fetchPositionInterval.current) clearInterval(fetchPositionInterval.current);
  };

  useEffect(() => {
    return () => cleanup();
  }, []);

  useDeepCompareEffect(() => {
    if (units.length > 0) {
      if (map) {
        if (units.length === 1) {
          const [firstUnit] = units;
          if (firstUnit?.latitude != null && firstUnit?.longitude != null) {
            var ll = L.latLng(firstUnit?.latitude, firstUnit?.longitude);
            map.flyToBounds(ll.toBounds(800), {});
          }
        } else {
          let bounds = L.latLngBounds(units.map((loc) => [loc.latitude, loc.longitude]));
          map.fitBounds(bounds);
        }
      }
      cleanup();
      if (useLiveUnitFetchInterval) {
        fetchPositionInterval.current = setInterval(() => {
          startLiveUnitRerender(true);
        }, UNIT_FETCH_INTERVAL);
      }
    }
  }, [
    (units || []).map((unit) => unit?.unitId),
    setMapPositionToUnitBounds,
    useLiveUnitFetchInterval
  ]);

  useEffect(() => {
    if (units.length === 1) {
      const unit = units[0];
      if (sharedLinkUnitLocation && unit?.unitId && isDesktop) {
        var ll = L.latLng(unit.latitude, unit.longitude);
        zoomMapToUnit(ll);
        setOpenSharedLinkUnitInformationSlidingPane(true);
      } else if (sharedLinkUnitLocation && unit?.unitId && !isDesktop) {
        setOpenSharedLinkUnitInformationSlidingPane(false);
        setOpenSharedLinkUnitInformationTooltip(true);
        var ll = L.latLng(unit.latitude, unit.longitude);
        zoomMapToUnitMobile(ll);
      }
    }
  }, [sharedLinkUnitLocation, units, isDesktop]);

  // useDeepCompareEffect(() => {
  //     if (units.length === 1 && ) {
  //         const unit = units[0];
  //         zoomMapToUnit([unit?.latitude, unit?.longitude])
  //     }
  // }, [
  //     units?.map((unit) => ({latitude: unit?.latitude, longitude: unit?.longitude}))
  // ])

  const displayMarkers = () => {
    setMarkersComponents(
      units
        .filter(
          (unit) =>
            unit &&
            unit?.latitude != null &&
            unit?.longitude != null &&
            (map ? map.getBounds().contains(L.latLng(unit.latitude, unit.longitude)) : true)
        )
        .map((unit) => (
          <div key={unit.unitId}>
            <Marker
              // pmIgnore={true}
              zIndexOffset={800}
              riseOnHover={true}
              icon={L.divIcon({
                className: 'custom-div-icon',
                html: renderToString(
                  <EventMarkerPulse
                    unit={unit}
                    iconSize={30}
                    iconColor="rgba(128, 128, 128, 0.6)"
                  />
                ),
                iconSize: [30, 30],
                iconAnchor: [15, 15],
                popupAnchor: [0, -18]
              })}
              position={[unit.latitude, unit.longitude]}
              eventHandlers={{
                click: (e) => {
                  if (sharedLinkUnitLocation && isDesktop) {
                    setOpenSharedLinkUnitInformationSlidingPane((prevState) => !prevState);
                  }
                  if (sharedLinkUnitLocation && !isDesktop) {
                    setOpenSharedLinkUnitInformationTooltip((prevState) => !prevState);
                    zoomMapToUnitMobile(e.latlng);
                  }
                  if (isDesktop) {
                    zoomMapToUnit(e.latlng);
                  }
                }
              }}
            >
              {usePopovers && isDesktop && (
                <Tooltip
                  permanent={usePermanentPopover}
                  direction={'bottom'}
                  offset={[0, 10]}
                  className={'unit-map-label'}
                >
                  <b className="text-center" style={{ fontSize: '8px' }}>
                    {unit?.alias?.toUpperCase() || unit.unitName.toUpperCase()}
                  </b>
                  {unit.fullImageUrl && (
                    <>
                      <br /> <br />
                      <Media
                        src={unit.fullImageUrl}
                        height="90"
                        width="90"
                        style={{
                          display: 'block',
                          marginLeft: 'auto',
                          marginRight: 'auto'
                        }}
                      />
                    </>
                  )}
                </Tooltip>
              )}
              {sharedLinkUnitLocation && !isDesktop && openSharedLinkUnitInformationTooltip && (
                <Tooltip
                  permanent={usePermanentPopover}
                  direction={'top'}
                  offset={[0, -20]}
                  className={'shared-link-unit-tooltip'}
                >
                  <div
                    style={{
                      marginBottom: '8px',
                      textAlign: 'center',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}
                  >
                    <b className="text-center" style={{ fontSize: '12px', marginBottom: '2px' }}>
                      {unit?.alias?.toUpperCase() || unit.unitName.toUpperCase()}
                    </b>
                  </div>
                  <Row>
                    <Col>
                      <Label className="shared-link-unit-label-information">
                        <MapPin color={primary} size={12} style={{ marginRight: '0.2rem' }} />
                        {intl.formatMessage({ id: 'units.locationName' })}
                      </Label>
                      <Label className="shared-link-unit-label-value-information">
                        {!isEmpty(unit.locationName) ? unit.locationName : 'N/A'}
                      </Label>
                    </Col>
                  </Row>
                  <hr style={{ margin: '0px 12px' }} />
                  <Row>
                    <Col>
                      <Label className="shared-link-unit-label-information">
                        <Clock color={primary} size={12} style={{ marginRight: '0.2rem' }} />
                        {intl.formatMessage({ id: 'units.gpsDate' })}
                      </Label>
                      <Label className="shared-link-unit-label-value-information">
                        {unit.gpsDate ? moment(unit.gpsDate).format('YYYY-MM-DD HH:mm') : 'N/A'}
                      </Label>
                    </Col>
                  </Row>
                  <hr style={{ margin: '0px 12px' }} />
                  <Row>
                    <Col>
                      <Label className="shared-link-unit-label-information">
                        <Clock color={primary} size={12} style={{ marginRight: '0.2rem' }} />
                        {intl.formatMessage({ id: 'units.lastCommunicationDate' })}
                      </Label>
                      <Label className="shared-link-unit-label-value-information">
                        {unit.lastCommunicationDate
                          ? moment(unit.lastCommunicationDate).format('YYYY-MM-DD HH:mm')
                          : 'N/A'}
                      </Label>
                    </Col>
                  </Row>
                  <hr style={{ margin: '0px 12px' }} />
                  <Row>
                    <Col>
                      <Label className="shared-link-unit-label-information">
                        <Menu color={primary} size={12} style={{ marginRight: '0.2rem' }} />
                        {intl.formatMessage({ id: 'units.privateCode' })}
                      </Label>
                      <Label className="shared-link-unit-label-value-information">
                        {!isEmpty(unit.privateCode) ? unit.privateCode : 'N/A'}
                      </Label>
                    </Col>
                  </Row>
                  <hr style={{ margin: '0px 12px' }} />
                  <Row>
                    <Col>
                      <Label className="shared-link-unit-label-information">
                        <Icon
                          icon={'Frontier'}
                          color={primary}
                          size={15}
                          style={{ marginRight: '0.2rem' }}
                        />
                        {intl.formatMessage({ id: 'units.odometer' })}
                      </Label>
                      <Label className="shared-link-unit-label-value-information">
                        {unit.odometerFormat || 'N/A'}{' '}
                      </Label>
                    </Col>
                  </Row>
                  <hr style={{ margin: '0px 12px' }} />
                  <Row>
                    <Col>
                      <Label className="shared-link-unit-label-information">
                        <Icon
                          icon={'Speedometer'}
                          color={primary}
                          size={12}
                          style={{ marginRight: '0.2rem' }}
                        />
                        {intl.formatMessage({ id: 'units.speed' })}
                      </Label>
                      <Label className="shared-link-unit-label-value-information">
                        {unit.speedFormat || 'N/A'}
                      </Label>
                    </Col>
                  </Row>
                  <hr style={{ margin: '0px 12px' }} />
                  <Row>
                    <Col>
                      <Label className="shared-link-unit-label-information mr-2">
                        <Settings color={primary} size={12} style={{ marginRight: '0.2rem' }} />
                        {intl.formatMessage({ id: 'units.status' })}
                      </Label>
                      <Badge
                        pill
                        style={{
                          ...GetMainStateStyle(GetMainStateById(unit?.mainStateId ?? -1)),
                          margin: '4px 12px',
                          fontSize: '12px'
                        }}
                      >
                        {GetMainStateName(
                          GetMainStateById(unit?.mainStateId),
                          intl.formatMessage({ id: GetMainStateById(unit.mainStateId).intl }),
                          unit.mainStateDate,
                          unit.speed,
                          unit.downtimeReasonDescription,
                          moment(unit.MainStateDate).fromNow()
                        ).length >= 25
                          ? GetMainStateName(
                              GetMainStateById(unit?.mainStateId),
                              intl.formatMessage({ id: GetMainStateById(unit.mainStateId).intl }),
                              unit.mainStateDate,
                              unit.speed,
                              unit.downtimeReasonDescription,
                              moment(unit.MainStateDate).fromNow()
                            ).substring(0, 25) + '...'
                          : GetMainStateName(
                              GetMainStateById(unit?.mainStateId),
                              intl.formatMessage({ id: GetMainStateById(unit.mainStateId).intl }),
                              unit.mainStateDate,
                              unit.speed,
                              unit.downtimeReasonDescription,
                              moment(unit.MainStateDate).fromNow()
                            )}
                      </Badge>
                    </Col>
                  </Row>
                  <hr style={{ margin: '0px 12px' }} />
                  <Row>
                    <Col>
                      <Label className="shared-link-unit-label-information">
                        <Icon
                          icon={'Target'}
                          color={primary}
                          size={15}
                          style={{ marginRight: '0.2rem' }}
                        />
                        {intl.formatMessage({ id: 'units.lastEvent' })}
                      </Label>
                      <Media id={'lastEventBadge'} className={'d-flex align-items-center'}>
                        {unit?.intlLastEvent?.length >= 15 && (
                          <UncontrolledTooltip target={'lastEventBadge'}>
                            <FormattedMessage id={`organizations.event${unit?.eventId ?? 0}`} />
                          </UncontrolledTooltip>
                        )}
                        <Badge
                          pill
                          className="mt-25 mr-50"
                          style={{
                            color: '#' + (unit.eventTextColor || '000000'),
                            backgroundColor: '#' + unit.eventColor,
                            display: 'inline-block',
                            fontSize: '12px',
                            margin: '4px 12px'
                          }}
                        >
                          {unit.eventName || unit.eventId
                            ? `${
                                unit?.intlLastEvent.length >= 15
                                  ? unit?.intlLastEvent?.substring(0, 15) + '...'
                                  : unit.eventId != null
                                  ? unit?.intlLastEvent
                                  : ''
                              }`
                            : ''}
                        </Badge>
                      </Media>
                    </Col>
                  </Row>
                </Tooltip>
              )}
            </Marker>
          </div>
        ))
    );
  };

  const displayObjects = () => {
    setHistoricalPositionsOnMapBounds({
      positions: unitHistoricalPositions?.positions?.filter(
        (position, index) =>
          position?.latitude != null &&
          position?.latitude !== 0 &&
          position?.longitude != null &&
          position?.longitude !== 0 &&
          (map
            ? map.getBounds().contains(L.latLng(position.latitude, position.longitude))
            : true) &&
          (map?.getZoom() > 14 || (map?.getZoom() > 10 && index % 10 === 0) || index % 20 === 0)
      ),
      parkedPositions: unitHistoricalPositions?.parkedPositions?.filter(
        (position, index) =>
          position?.latitude != null &&
          position?.latitude !== 0 &&
          position?.longitude != null &&
          position?.longitude !== 0 &&
          (map
            ? map.getBounds().contains(L.latLng(position.latitude, position.longitude))
            : true) &&
          (map?.getZoom() > 14 || (map?.getZoom() > 10 && index % 10 === 0) || index % 20 === 0)
      ),
      polyline:
        unitHistoricalPositions?.polyline?.length > 0
          ? map
              .getBounds()
              .intersects(
                unitHistoricalPositions?.polyline?.map((position) => [
                  position.latitude,
                  position.longitude
                ])
              )
            ? unitHistoricalPositions.polyline
            : []
          : undefined,
      consoleEvents: showConsoleEvents && consoleEvents ? consoleEvents : []
    });
  };

  useDeepCompareEffect(() => {
    if (showUnits) {
      displayMarkers();
    }
  }, [
    showUnits,
    units?.map((unit) => ({
      latitude: unit?.latitude,
      longitude: unit?.longitude,
      isShownInMap: unit?.isShownInMap
    }))
  ]);

  useEffect(() => {
    if (
      showHistoricalPositions &&
      sharedLinkRoute &&
      (unitHistoricalPositions?.positions !== null ||
        unitHistoricalPositions?.positions !== undefined)
    ) {
      zoomMapToPositions(unit);
      displayObjects();
      setUnitPositionHistoryReady(true);
    }
  }, [showHistoricalPositions, sharedLinkRoute, unitHistoricalPositions]);

  return (
    <>
      {showUnits && markersComponents}
      {sharedLinkRoute && showHistoricalPositions && (
        <>
          {historicalPositionsOnMapBounds?.positions?.length > 0 &&
            historicalPositionsOnMapBounds.positions.map((position, index) =>
              position.eventId === 0 ? (
                <RotatedMarker
                  key={`${index}-${position.id}`}
                  // pmIgnore={true}
                  zIndexOffset={750}
                  riseOnHover={true}
                  rotationAngle={position.heading}
                  rotationOrigin="center"
                  icon={L.divIcon({
                    className: 'custom-div-icon',
                    html: `<svg viewBox="0 0 58 58">
                  <polygon style="fill:${primary}; stroke:black; stroke-width: 2" 
                  points="28.2,34.4 14.5,44.1 28.2,7.2 40.6,44.1 "/></svg>`,
                    iconSize: [22, 22],
                    iconAnchor: [11, 11]
                  })}
                  position={[position.latitude, position.longitude]}
                  eventHandlers={{
                    click: () => {}
                  }}
                >
                  <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                    <div style={{ fontSize: '10px', lineHeight: '1.3' }}>
                      <div style={{ fontWeight: 'bold' }}>
                        {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                      </div>
                      {position.locationName && (
                        <div>{position.locationName}</div>
                      )}
                      {position.speed != null && (
                        <div>{position.speed} km/h</div>
                      )}
                    </div>
                  </Tooltip>
                  <Popup closeButton={true} closeOnClick={false} autoClose={false}>
                    <div style={{ minWidth: '200px', fontSize: '11px' }}>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>{intl.formatMessage({ id: 'trackingUnits.gpsDate' })}:</strong>{' '}
                        {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                      </div>
                      {position.systemDate && (
                        <div style={{ marginBottom: '5px' }}>
                          <strong>{intl.formatMessage({ id: 'units.receptionDate' })}:</strong>{' '}
                          {moment(position.systemDate).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                      )}
                      {position.locationName && (
                        <div style={{ marginBottom: '5px' }}>
                          <strong>{intl.formatMessage({ id: 'units.locationName' })}:</strong>{' '}
                          {position.locationName}
                        </div>
                      )}
                      {position.speed != null && (
                        <div style={{ marginBottom: '5px' }}>
                          <strong>{intl.formatMessage({ id: 'units.speed' })}:</strong> {position.speed} km/h
                        </div>
                      )}
                      {position.odometer != null && (
                        <div style={{ marginBottom: '5px' }}>
                          <strong>{intl.formatMessage({ id: 'units.odometer' })}:</strong> {position.odometer?.toFixed(2)} km
                        </div>
                      )}
                      <div style={{ fontSize: '10px', color: '#666', marginTop: '8px' }}>
                        {position.latitude?.toFixed(6)}, {position.longitude?.toFixed(6)}
                      </div>
                    </div>
                  </Popup>
                </RotatedMarker>
              ) : (
                (showUnitHistoryOnlyNormalPositionsMarkers ?? false) == false && (
                  <Marker
                    key={`${index}-${position.id}`}
                    // pmIgnore={true}
                    zIndexOffset={700}
                    riseOnHover={true}
                    icon={L.divIcon({
                      className: 'custom-div-icon',
                      html: renderToString(
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            background: 'white',
                            width: 30,
                            height: 30,
                            padding: 3,
                            borderRadius: '100%',
                            borderStyle: 'solid',
                            borderColor: `#${position.eventColor}`
                          }}
                        >
                          <Icon
                            icon={`Alert-${position.eventId}`}
                            color={`#${position.eventColor}`}
                            size={30}
                          />
                        </div>
                      ),
                      iconSize: [30, 30],
                      iconAnchor: [15, 15]
                    })}
                    position={[position.latitude, position.longitude]}
                    eventHandlers={{
                      click: () => {}
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                      <div style={{ fontSize: '10px', lineHeight: '1.3' }}>
                        <div style={{ fontWeight: 'bold' }}>
                          {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                        {position.eventName && (
                          <div>
                            {position.eventName}
                            {position.eventParameter && ` (${position.eventParameter})`}
                            {position.paramAlerta && ` (${position.paramAlerta})`}
                          </div>
                        )}
                        {position.locationName && (
                          <div>{position.locationName}</div>
                        )}
                        {position.speed != null && (
                          <div>{position.speed} km/h</div>
                        )}
                      </div>
                    </Tooltip>
                    <Popup closeButton={true} closeOnClick={false} autoClose={false}>
                      <div style={{ minWidth: '200px', fontSize: '11px' }}>
                        <div style={{ marginBottom: '5px' }}>
                          <strong>{intl.formatMessage({ id: 'trackingUnits.gpsDate' })}:</strong>{' '}
                          {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                        {position.systemDate && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.receptionDate' })}:</strong>{' '}
                            {moment(position.systemDate).format('YYYY-MM-DD HH:mm:ss')}
                          </div>
                        )}
                        {position.eventName && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.event' })}:</strong>{' '}
                            {position.eventName}
                            {position.eventParameter && ` (${position.eventParameter})`}
                            {position.paramAlerta && ` (${position.paramAlerta})`}
                          </div>
                        )}
                        {position.locationName && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.locationName' })}:</strong>{' '}
                            {position.locationName}
                          </div>
                        )}
                        {position.speed != null && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.speed' })}:</strong> {position.speed} km/h
                          </div>
                        )}
                        {position.odometer != null && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.odometer' })}:</strong> {position.odometer?.toFixed(2)} km
                          </div>
                        )}
                        <div style={{ fontSize: '10px', color: '#666', marginTop: '8px' }}>
                          {position.latitude?.toFixed(6)}, {position.longitude?.toFixed(6)}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                )
              )
            )}
          {historicalPositionsOnMapBounds?.parkedPositions?.length > 0 &&
            historicalPositionsOnMapBounds.parkedPositions.map((position, index) => (
              <Marker
                key={`${index}-${position.id}`}
                // pmIgnore={true}
                zIndexOffset={700}
                riseOnHover={true}
                icon={L.divIcon({
                  className: 'custom-div-icon',
                  html: renderToString(
                    <Icon
                      icon={getIconByDatesDifference(position.endDate, position.gpsDate)}
                      size={48}
                    />
                  ),
                  iconSize: [30, 30],
                  iconAnchor: [15, 15]
                })}
                position={[position.latitude, position.longitude]}
                eventHandlers={{
                  click: () => {}
                }}
              >
                <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                  <div style={{ fontSize: '10px', lineHeight: '1.3' }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                    </div>
                    <div>
                      {intl.formatMessage({ id: 'units.parked' })}
                    </div>
                    {position.locationName && (
                      <div>{position.locationName}</div>
                    )}
                  </div>
                </Tooltip>
                <Popup closeButton={true} closeOnClick={false} autoClose={false}>
                  <div style={{ minWidth: '200px', fontSize: '11px' }}>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>{intl.formatMessage({ id: 'trackingUnits.gpsDate' })} ({intl.formatMessage({ id: 'units.start' })}):</strong>{' '}
                      {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>{intl.formatMessage({ id: 'trackingUnits.gpsDate' })} ({intl.formatMessage({ id: 'units.end' })}):</strong>{' '}
                      {moment(position.endDate).format('YYYY-MM-DD HH:mm:ss')}
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>{intl.formatMessage({ id: 'units.duration' })}:</strong>{' '}
                      {getDatesDifferenceString(position.endDate, position.gpsDate, intl)}
                    </div>
                    {position.locationName && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong>{intl.formatMessage({ id: 'units.locationName' })}:</strong>{' '}
                        {position.locationName}
                      </div>
                    )}
                    {position.odometer != null && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong>{intl.formatMessage({ id: 'units.odometer' })}:</strong> {position.odometer?.toFixed(2)} km
                      </div>
                    )}
                    <div style={{ fontSize: '10px', color: '#666', marginTop: '8px' }}>
                      {position.latitude?.toFixed(6)}, {position.longitude?.toFixed(6)}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          {historicalPositionsOnMapBounds?.polyline?.length > 0 && (
            <Polyline
              pathOptions={{ color: primary, weight: 3 }}
              positions={historicalPositionsOnMapBounds.polyline.map((point) => [
                point.latitude,
                point.longitude
              ])}
              eventHandlers={{
                click: () => {}
              }}
            ></Polyline>
          )}
          {historicalPositionsOnMapBounds?.consoleEvents?.length > 0 &&
            showConsoleEvents &&
            historicalPositionsOnMapBounds.consoleEvents.map((position, index) => (
              <Marker
                key={`${index}-${position.id}`}
                // pmIgnore={true}
                zIndexOffset={700}
                riseOnHover={true}
                icon={L.divIcon({
                  className: 'custom-div-icon',
                  html: renderToString(
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        background: 'white',
                        width: 30,
                        height: 30,
                        padding: 3,
                        borderRadius: '100%',
                        borderStyle: 'solid',
                        borderColor: `#${position.eventColor}`
                      }}
                    >
                      <Icon
                        icon={`Alert-${position.eventId}`}
                        color={`#${position.eventColor}`}
                        size={30}
                      />
                    </div>
                  ),
                  iconSize: [30, 30],
                  iconAnchor: [15, 15]
                })}
                position={[position.latitude, position.longitude]}
                eventHandlers={{
                  click: () => {}
                }}
              >
                <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                  <div style={{ fontSize: '10px', lineHeight: '1.3' }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                    </div>
                    <div>
                      {intl.formatMessage({
                        id: `organizations.event${position.eventId}`
                      })}
                      {position.eventParameter && ` (${position.eventParameter})`}
                      {position.paramAlerta && ` (${position.paramAlerta})`}
                    </div>
                    {position.locationName && (
                      <div>{position.locationName}</div>
                    )}
                    {position.speed != null && (
                      <div>{position.speed} km/h</div>
                    )}
                  </div>
                </Tooltip>
                <Popup closeButton={true} closeOnClick={false} autoClose={false}>
                  <div style={{ minWidth: '200px', fontSize: '11px' }}>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>{intl.formatMessage({ id: 'trackingUnits.gpsDate' })}:</strong>{' '}
                      {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                    </div>
                    {position.systemDate && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong>{intl.formatMessage({ id: 'units.receptionDate' })}:</strong>{' '}
                        {moment(position.systemDate).format('YYYY-MM-DD HH:mm:ss')}
                      </div>
                    )}
                    <div style={{ marginBottom: '5px' }}>
                      <strong>{intl.formatMessage({ id: 'units.event' })}:</strong>{' '}
                      {intl.formatMessage({
                        id: `organizations.event${position.eventId}`
                      })}
                      {position.eventParameter && ` (${position.eventParameter})`}
                      {position.paramAlerta && ` (${position.paramAlerta})`}
                    </div>
                    {position.locationName && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong>{intl.formatMessage({ id: 'units.locationName' })}:</strong>{' '}
                        {position.locationName}
                      </div>
                    )}
                    {position.speed != null && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong>{intl.formatMessage({ id: 'units.speed' })}:</strong> {position.speed} km/h
                      </div>
                    )}
                    {position.odometer != null && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong>{intl.formatMessage({ id: 'units.odometer' })}:</strong> {position.odometer?.toFixed(2)} km
                      </div>
                    )}
                    <div style={{ fontSize: '10px', color: '#666', marginTop: '8px' }}>
                      {position.latitude?.toFixed(6)}, {position.longitude?.toFixed(6)}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
        </>
      )}
      {sharedLinkUnitLocation && units.length === 1 && (
        <SlidingPaneSharedLinkUnitInformation
          unit={units[0]}
          isSlidingPaneOpen={openSharedLinkUnitInformationSlidingPane}
          closeSlidingPane={() => setOpenSharedLinkUnitInformationSlidingPane(false)}
        />
      )}
    </>
  );
};

MapUnitsPublic.defaultProps = {
  showUnits: true,
  units: [],
  showHistoricalPositions: false,
  useLiveUnitFetchInterval: false,
  setMapPositionToUnitBounds: false,
  setMapPositionToPositionsBounds: false,
  sharedLinkUnitLocation: false,
  sharedLinkRoute: false,
  usePopovers: true,
  usePermanentPopover: false,
  unitHistoricalPositions: {},
  mapOnSlidingPane: false,
  showConsoleEvents: false,
  consoleEvents: []
};

export default MapUnitsPublic;
